<template>
  <div>
    <div class="d-flex align-center">
      <div class="img-preview mr-4">
        <v-skeleton-loader
          v-if="isLoading"
          :height="requiredDimensions ? 50 : height"
          :width="requiredDimensions ? 150 : width"
          type="card"
        ></v-skeleton-loader>
        <div v-else>
          <div v-if="imageUrl && imageUrl.split('.').pop().includes('pdf')" id="pdf-upload-image"></div>
          <v-img
            v-else
            :height="requiredDimensions ? 50 : height"
            :width="requiredDimensions ? 150 : width"
            :src="imageUrl || '/default-img.png'"
            class="rounded-sm"
          ></v-img>
        </div>
      </div>
      <v-sheet v-if="requiredDimensions" outlined color="grey lighten-1" class="mr-4" rounded>
        <v-card outlined elevation="0" class="img-dimensions d-flex align-center text-body-1 pa-3 black--text">
          <v-icon medium color="black" class="pr-2"> mdi-image-outline </v-icon>{{ requiredWidth }}px x
          {{ requiredHeight }}px</v-card
        >
      </v-sheet>

      <div>
        <div>
          <v-file-input
            :disabled="isLoading"
            :accept="accept"
            v-model="file"
            class="file-input"
            :rules="rules"
            ref="myFile"
            @change="selectedFile"
            @click="$event.target.value = null"
            truncate-length="15"
          ></v-file-input>

          <v-btn
            :disabled="isLoading"
            large
            depressed
            color="grey lighten-4"
            class="mr-4 text-capitalize font-weight-bold black--text"
            height="36"
            @click="triggerFileInput"
          >
            <span class="mr-2">Choose {{ allowPdf ? `File` : `Image` }}</span>
          </v-btn>

          <v-btn
            :disabled="isLoading"
            large
            depressed
            color="grey lighten-4"
            class="text-capitalize font-weight-bold black--text"
            height="36"
            @click="clearImage()"
          >
            <span class="mr-2">Clear Image</span>
          </v-btn>
        </div>

        <p v-if="showErrorMessage" class="error--text mt-2" style="margin: 0; max-width: 340px">
          {{ errorMessage }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import { fileDelete, fileUpload } from '@/utils/aws';
  import { mapActions } from 'vuex';
  import PDFObject from 'pdfobject';

  export default Vue.extend({
    name: 'UploadImage',
    props: {
      fileUrl: {
        type: String,
        default: '',
        required: false,
      },
      requiredHeight: {
        type: Number,
        default: null,
        required: false,
      },
      requiredWidth: {
        type: Number,
        default: null,
        required: false,
      },
      clientId: {
        type: Number,
        required: false,
      },
      value: {
        type: String,
        default: '',
      },
      rules: {
        type: Array,
      },
      height: {
        type: String,
        default: '60',
        required: false,
      },
      width: {
        type: String,
        default: '60',
        required: false,
      },
      allowPdf: {
        type: Boolean,
        default: false,
        required: false,
      },
      errorMessage: {
        type: String,
        default: 'Error',
        required: false,
      },
      showErrorMessage: {
        type: Boolean,
        default: false,
        required: false,
      },
    },
    data: function () {
      let file: File = null;

      return {
        imageUrl: this.fileUrl || null,
        maxFileSize: 10000000,
        file,
        isLoading: false,
      };
    },
    watch: {
      fileUrl: {
        handler(val) {
          this.imageUrl = val;

          if (val && val.split('.').pop().includes('pdf')) {
            this.$nextTick().then(() => {
              PDFObject.embed(val, '#pdf-upload-image', { forceIframe: true, pdfOpenParams: { toolbar: 1 } });
            });
          }
        },
        immediate: true,
      },
    },
    computed: {
      accept() {
        if (this.allowPdf) return 'image/*,.pdf';
        return 'image/*';
      },
      requiredDimensions(): boolean {
        return !!this.requiredHeight && !!this.requiredWidth;
      },
      s3Folder(): string {
        return `digital_programs/${this.clientId || 0}/images`;
      },
    },
    methods: {
      ...mapActions('ui', { showToast: 'showToast' }),

      selectedFile() {
        let file: File = this.file;

        if (!file) return;

        if (file.size > this.maxFileSize) {
          this.showToast(`The image size must be lower than 10mb`);
          return false;
        }

        let reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = async (evt) => {
          let file: File = this.file;

          if (file.type.indexOf('image/') === 0) {
            let img = new Image();

            img.onload = async () => {
              await this.checkDimensions(img);
            };

            img.src = evt.target.result as string;
          }

          this.uploadFile(file);
        };

        // reader.onerror = (evt) => {
        //   console.error(evt);
        // };
      },
      async uploadFile(file: File) {
        try {
          this.isLoading = true;
          this.$emit('loading-image', true);

          const result = await fileUpload(file, this.s3Folder, this.clientId);

          if (result.status !== 200) {
            this.showToast('Something went wrong');
          } else {
            this.imageUrl = result.file;
            this.$emit('input', this.imageUrl);
          }
        } catch (e) {
          console.log(e);
        } finally {
          this.isLoading = false;
          this.$emit('loading-image', false);
        }
      },
      triggerFileInput() {
        ((this.$refs.myFile as Vue).$el.querySelector('input[type="file"]') as HTMLInputElement).click();
      },
      clearImage() {
        this.imageUrl = '';
        this.$emit('input', this.imageUrl);
        fileDelete(this.value, this.clientId);
      },
      async checkDimensions(img: HTMLImageElement) {
        if (this.requiredDimensions) {
          if (img.width !== this.requiredWidth || img.height !== this.requiredHeight) {
            this.showToast(`The image dimensions must be ${this.requiredWidth}px x ${this.requiredHeight}px`);
            return false;
          }
        }
      },
    },
  });
</script>
<style lang="sass" scoped>
  input[type='file']
    display: none

  .custom-file-upload
    display: inline-block
    padding: 6px 12px
    cursor: pointer
    font-size: 0.875rem
    white-space: nowrap

  .img-dimensions
    width: 380px
    height: 41px

  .file-input
    display: none

  #pdf
    border: 1px solid gray
    min-height: 255px // min-height to show toolbar
    min-width: 425px // min-width to show toolbar
    display: grid
</style>
